<template>
  <ContractReviewDisplayComponent
    :accountData="accountData"
    :contractsToShow="contracts"
    :signerFields="signerFields"
    :companyFields="companyFields"
    :generatorFields="generatorFields"
    :contractState="contractState"
    :hasGenerator="hasGenerator"
    @createContainerForStartSigningReady="
      $emit('createContainerForStartSigningReady', $event)
    "
  />
</template>

<script setup lang="ts">
import { useCompanyFinder } from "@/composables/useCompanyFinder";
import { Account } from "@/types";
import { useOptionalContractInclusion } from "@/signup/composables/useOptionalContractInclusion";
import { reactive, computed } from "vue";
import ContractReviewDisplayComponent from "@/signup/ContractReviewDisplayComponent.vue";
import { useI18n } from "vue-i18n";
import {
  PL_AUTHORIZATION_APPLICATION,
  EN_SOLDERA_MEMBERSHIP,
} from "@/signup/contract_keys";

const props = defineProps<{
  accountData: Account;
}>();

// WORKAROUND: disableChangingInformation is declared but deliberately not forwarded from
// ContractReviewDisplayComponent, so the PL signup flow never hears it. Declaring it is what keeps
// the parent's listener out of $attrs — dropping it from here would wire the event up by
// fallthrough, which is the thing to avoid. The display component raises it whenever *any*
// company document is completed, and PL is the one flow where a completed document need not
// match the container it offers: the membership agreement is admin-attached under
// pl_soldera_membership_agreement while the container offers EN_SOLDERA_MEMBERSHIP. In that
// state the lock fires, _checkAllCompleted stays false so nothing redirects, and the flow
// hides its own "start signing" button. Forwarding can land once the display component bases
// the lock on documents matching the shown containers rather than on all of them.
defineEmits<{
  createContainerForStartSigningReady: [
    createContainerForStartSigning: () => Promise<void>,
  ];
  disableChangingInformation: [];
}>();

const { t } = useI18n();
const signerFields = reactive([
  { label: t("account.name"), value: props.accountData.name },
  { label: t("account.email"), value: props.accountData.email },
  {
    label: t("account.phone_number"),
    value: props.accountData.phone_number,
  },
]);
const { companyIndex } = useCompanyFinder(props.accountData);
const selectedCompany = computed(
  () => props.accountData.companies[companyIndex.value]
);
const companyFields = reactive([
  { label: t("company.name"), value: selectedCompany.value.name },
  {
    label: t("company.registry_code"),
    value: selectedCompany.value.registry_code,
  },
  { label: t("company.vat"), value: selectedCompany.value.vat },
  { label: t("company.address"), value: selectedCompany.value.address },
]);

// Empty generator fields since PL doesn't use generators
const generatorFields = reactive([]);
const hasGenerator = computed(() => false);

const contractState = computed(() => 1); // Always in authorization mode

const { includeDocument } = useOptionalContractInclusion(
  selectedCompany,
  contractState
);

// Build contracts list
const contractsList = [
  {
    id: 1,
    key: PL_AUTHORIZATION_APPLICATION,
    contract_title: t(
      "signup.contract_review_component.pl.pl_authorization_application_title"
    ),
    contract_description: t(
      "signup.contract_review_component.pl.pl_authorization_application_text"
    ),
    include: includeDocument([1], PL_AUTHORIZATION_APPLICATION).value,
    can_include: true,
  },
  {
    id: 2,
    key: EN_SOLDERA_MEMBERSHIP,
    contract_title: t(
      "signup.contract_review_component.pl.pl_soldera_membership_agreement_title"
    ),
    contract_description: t(
      "signup.contract_review_component.pl.pl_soldera_membership_agreement_text"
    ),
    include: includeDocument([1], "pl_soldera_membership_agreement").value,
    can_include: true,
  },
];

const contracts = reactive([[...contractsList]]);
</script>
